export interface ProcessVoiceOptions {
  onSpeakingChange?: (isSpeaking: boolean) => void;
}

export interface ProcessedVoice {
  stream: MediaStream;
  setMuted: (muted: boolean) => void;
  stop: () => void;
}

// Browser echo cancellation and noise suppression run on the captured track first.
// This gate then keeps steady room noise from being sent between spoken phrases.
export function processVoice(input: MediaStream, options?: ProcessVoiceOptions): ProcessedVoice {
  const AudioContextClass = typeof window === 'undefined'
    ? undefined
    : window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

  if (!AudioContextClass) {
    return {
      stream: input,
      setMuted: (muted) => input.getAudioTracks().forEach((track) => { track.enabled = !muted; }),
      stop: () => input.getTracks().forEach((track) => track.stop()),
    };
  }

  let context: AudioContext | null = null;
  try {
    context = new AudioContextClass();
    if (context.state === 'suspended') {
      void context.resume().catch(() => {});
    }
    const source = context.createMediaStreamSource(input);
    const highpass = context.createBiquadFilter();
    highpass.type = 'highpass';
    highpass.frequency.value = 100;
    const analyser = context.createAnalyser();
    analyser.fftSize = 512;
    const gain = context.createGain();
    gain.gain.value = 0;
    const destination = context.createMediaStreamDestination();
    source.connect(highpass);
    highpass.connect(analyser);
    analyser.connect(gain);
    gain.connect(destination);

    const samples = new Float32Array(analyser.fftSize);
    let muted = false;
    let noiseFloor = 0.004;
    let lastSpeechAt = 0;
    let lastSpeakingState = false;

    const timer = window.setInterval(() => {
      if (context?.state === 'suspended') {
        void context.resume().catch(() => {});
      }
      analyser.getFloatTimeDomainData(samples);
      let power = 0;
      for (const sample of samples) power += sample * sample;
      const level = Math.sqrt(power / samples.length);
      const now = Date.now();
      const speaking = level > Math.max(0.012, noiseFloor * 2.5);
      if (speaking) lastSpeechAt = now;
      else if (now - lastSpeechAt > 350) noiseFloor = noiseFloor * 0.98 + Math.min(level, 0.04) * 0.02;
      const open = !muted && (speaking || now - lastSpeechAt < 280);
      gain.gain.setTargetAtTime(open ? 1 : 0, context!.currentTime, open ? 0.012 : 0.045);

      if (options?.onSpeakingChange && open !== lastSpeakingState) {
        lastSpeakingState = open;
        options.onSpeakingChange(open);
      }
    }, 30);

    return {
      stream: destination.stream,
      setMuted: (nextMuted) => {
        muted = nextMuted;
        if (typeof input.getAudioTracks === 'function') {
          input.getAudioTracks().forEach((track) => { track.enabled = !nextMuted; });
        }
        if (muted) {
          gain.gain.setValueAtTime(0, context!.currentTime);
          if (options?.onSpeakingChange && lastSpeakingState) {
            lastSpeakingState = false;
            options.onSpeakingChange(false);
          }
        }
      },
      stop: () => {
        window.clearInterval(timer);
        if (options?.onSpeakingChange && lastSpeakingState) {
          lastSpeakingState = false;
          options.onSpeakingChange(false);
        }
        destination.stream.getTracks().forEach((track) => track.stop());
        input.getTracks().forEach((track) => track.stop());
        void context!.close();
      },
    };
  } catch {
    if (context) void context.close();
    return {
      stream: input,
      setMuted: (muted) => input.getAudioTracks().forEach((track) => { track.enabled = !muted; }),
      stop: () => input.getTracks().forEach((track) => track.stop()),
    };
  }
}
